import type { TelemetryReading } from '../../hooks/useTelemetry.ts';
import { level } from '../state.ts';

/* ==========================================================================
   The channel catalogue — what a telemetry reading actually contains, what
   each field is called, what it is measured in, and where its limits are.

   One definition per channel, published here rather than assumed by the
   operator: every instrument in the kit draws its limits from this table, so
   a battery rule sits at 15% in a plot, in the channel rail's health dot and
   in the transport bar's event ticks without any of the three being told
   twice. THE BAR §5 ("thresholds drawn where they mean something") and §13
   tell 15 ("a threshold that exists only in the operator's head").

   The numbers are the same ones `workspaces/fleet/views/RobotDetail.tsx`
   publishes for the same fields, deliberately — two surfaces disagreeing
   about where a robot's battery becomes a problem is worse than either
   number being wrong. That duplication should collapse onto this file.
   ========================================================================== */

function payloadNum(t: TelemetryReading, key: string): number | null {
  const v = t.payload[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/* `hooks/useTelemetry`'s row type predates four fields the contract actually
   returns — `seq`, `mono_ns`, `agent_boot_id` and `clock_offset_ms` — so they
   are read structurally here rather than by widening a type this piece does
   not own. Flagged for the coherence pass; robot detail reads them the same
   way for the same reason. */
export function readingNum(t: TelemetryReading, key: string): number | null {
  const v = (t as unknown as Record<string, unknown>)[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function readingStr(t: TelemetryReading, key: string): string | null {
  const v = (t as unknown as Record<string, unknown>)[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

export interface Channel {
  key: string;
  /** Short enough to survive a 132px rail cell. */
  label: string;
  unit: string;
  digits: number;
  pick: (t: TelemetryReading) => number | null;
  /** The metric's real domain, where it has one. Omitted ⇒ fitted box. */
  min?: number;
  max?: number;
  warn?: number;
  bad?: number;
  /** High is bad — CPU, temperature, disk used. */
  invert?: boolean;
  /** Judged on a different field than the one it draws (disk free/used). */
  judge?: (t: TelemetryReading) => number | null;
  /** Names that other field, so the card can say what the dot is reading. */
  judgeNote?: string;
  /** The value wraps — 359° → 0° is not a fall of 359 degrees. */
  wrap?: number;
  /** Fixed y ticks, where round numbers are not the useful ones (a compass). */
  ticks?: readonly number[];
  /**
   * How a tick prints, where the number is not the useful label (N/E/S/W).
   * `compact` is passed by a lane whose gutter is too narrow to hold the long
   * form — see `Frame.tight`.
   */
  tickText?: (v: number, compact: boolean) => string;
  /** Off by default: real, but not what an operator opens the page for. */
  secondary?: boolean;
  hint: string;
}

export const CHANNELS: readonly Channel[] = [
  {
    key: 'battery', label: 'Battery', unit: '%', digits: 1,
    pick: (t) => t.battery_pct, min: 0, max: 100, warn: 40, bad: 15,
    hint: 'State of charge. Warn at 40%, recover-now at 15%.',
  },
  {
    key: 'speed', label: 'Speed', unit: 'm/s', digits: 2,
    pick: (t) => t.speed_mps, min: 0,
    hint: 'Ground speed reported by the agent. No limit — speed is a fact, not a fault.',
  },
  {
    key: 'cpu', label: 'CPU', unit: '%', digits: 1,
    pick: (t) => t.cpu_pct, min: 0, max: 100, warn: 75, bad: 90, invert: true,
    hint: 'Agent host load. Warn at 75%, saturated at 90%.',
  },
  {
    key: 'temp', label: 'Temp', unit: '°C', digits: 1,
    pick: (t) => t.temp_c, min: 0, max: 90, warn: 65, bad: 80, invert: true,
    hint: 'Compute-bay temperature. Warn at 65 °C, throttling at 80 °C.',
  },
  {
    key: 'disk', label: 'Disk free', unit: 'GiB', digits: 1,
    pick: (t) => t.disk_free_gib,
    /* Free GiB is the number an operator acts on; the limit lives on used%,
       because per-robot capacity is not in the contract and a bounded
       free-space domain would be a guess. */
    judge: (t) => t.disk_used_pct, judgeNote: 'disk used %',
    warn: 80, bad: 92, invert: true,
    hint: 'Capture space left. Judged on disk used: warn at 80%, full at 92%.',
  },
  {
    key: 'rssi', label: 'Link', unit: 'dBm', digits: 0,
    pick: (t) => payloadNum(t, 'link_rssi_dbm'),
    min: -100, max: -40, warn: -75, bad: -85,
    hint: 'Uplink RSSI from the telemetry payload. Warn at −75 dBm, marginal at −85 dBm.',
  },
  {
    key: 'gnss', label: 'GNSS', unit: 'sats', digits: 0,
    pick: (t) => payloadNum(t, 'gnss_sats'),
    min: 0, max: 24, warn: 8, bad: 5,
    hint: 'Satellites in the fix. Warn at 8, position untrustworthy at 5.',
  },
  {
    key: 'heading', label: 'Heading', unit: '°', digits: 0,
    pick: (t) => t.heading_deg, min: 0, max: 360, wrap: 360,
    /* The compass, not round numbers: a bearing is read against N/E/S/W, and
       0 and 360 are the same place — so the box is always the whole circle
       and the ticks are the cardinals.

       DEGREE AND CARDINAL, ALWAYS BOTH. The cardinal alone reads well down a
       tall isolated lane (N · W · S · E · N) and is a defect on a 45px one,
       where only the box's two ends are printed and BOTH of them say `N` —
       an axis whose two ends carry the same label is an axis the operator
       cannot use. `skydio-remote-flight-deck-live.png` prints `18° N` for
       exactly this reason: the number is the measurement and the letter is
       the sense of it, and a heading readout that drops either is short. */
    ticks: [0, 90, 180, 270, 360],
    tickText: (v, compact) => {
      const rose = v === 0 || v === 360 ? 'N'
        : v === 90 ? 'E' : v === 180 ? 'S' : v === 270 ? 'W' : null;
      if (rose === null) return `${v}`;
      return compact ? rose : `${v} ${rose}`;
    },
    hint: 'Course over ground, true. Wraps at 360° — the trace breaks rather than falling.',
  },
  {
    key: 'skew', label: 'Clock skew', unit: 'ms', digits: 2, secondary: true,
    pick: (t) => readingNum(t, 'clock_offset_ms'),
    hint: 'Agent wall clock against the ingest tier. Sign is agent-ahead.',
  },
];

export const CHANNEL_BY_KEY: ReadonlyMap<string, Channel> = new Map(
  CHANNELS.map((c) => [c.key, c]),
);

/** The channels a first look at a robot is about. */
export const DEFAULT_CHANNELS: readonly string[] = CHANNELS.filter((c) => !c.secondary).map(
  (c) => c.key,
);

/* --------------------------------------------------------------------------
   Thresholds → a reading's level, honouring `judge` and `invert`.
   -------------------------------------------------------------------------- */

export function readingLevel(
  c: Channel,
  t: TelemetryReading,
): 'ok' | 'warn' | 'bad' | 'unknown' {
  if (c.warn === undefined || c.bad === undefined) {
    return c.pick(t) == null ? 'unknown' : 'ok';
  }
  const v = (c.judge ?? c.pick)(t);
  return level(v, c.warn, c.bad, c.invert === true);
}

/** The worst level any of these readings reached — the channel's verdict. */
export function worstLevel(
  c: Channel,
  readings: readonly TelemetryReading[],
): 'ok' | 'warn' | 'bad' | 'unknown' {
  let seen = false;
  let worst: 'ok' | 'warn' | 'bad' = 'ok';
  for (const t of readings) {
    const l = readingLevel(c, t);
    if (l === 'unknown') continue;
    seen = true;
    if (l === 'bad') return 'bad';
    if (l === 'warn') worst = 'warn';
  }
  return seen ? worst : 'unknown';
}

/**
 * Which end of the DRAWN value is the dangerous one.
 *
 * Not the same question as `invert`. `invert` says which end of the JUDGED
 * quantity is bad, and for one channel the two differ: disk is judged on
 * `disk_used_pct` (high is bad) but drawn as free GiB (LOW is bad). Anything
 * quoting the worst reading a window held has to answer this one, or a rail
 * reports a robot's fullest disk as its emptiest.
 */
export function dangerHigh(c: Channel): boolean {
  return c.invert === true && c.judge === undefined;
}

/* --------------------------------------------------------------------------
   Which clock the x-axis is.

   The agent stamps `ts` off its own clock and reports `clock_offset_ms`, the
   measured skew of that clock against the ingest tier, so the console holds
   two genuinely different timelines and has to say which one it is drawing
   (§13 tell 22 — an unnamed clock). `ingest_time = agent_ts − clock_offset`.
   -------------------------------------------------------------------------- */

export type TimeAxis = 'agent' | 'ingest';

export const AXIS_LABEL: Record<TimeAxis, string> = {
  agent: 'agent_time',
  ingest: 'ingest_time',
};

export function stampOf(t: TelemetryReading, axis: TimeAxis): number {
  const base = new Date(t.ts).getTime();
  if (axis === 'agent') return base;
  const skew = readingNum(t, 'clock_offset_ms');
  return skew === null ? base : base - skew;
}

/* --------------------------------------------------------------------------
   The window. An absolute span of wall clock, never a row count: 200 readings
   is eight hours on a 2m24s robot and half an hour on a 10s one, so two
   robots' plots could not be compared and one robot's plot would change
   meaning when its cadence did.
   -------------------------------------------------------------------------- */

export type SpanKey = '1h' | '6h' | '24h' | '7d';

export const SPANS: readonly { key: SpanKey; ms: number; label: string }[] = [
  { key: '1h', ms: 3_600_000, label: '1 hour' },
  { key: '6h', ms: 21_600_000, label: '6 hours' },
  { key: '24h', ms: 86_400_000, label: '24 hours' },
  { key: '7d', ms: 604_800_000, label: '7 days' },
];

export function spanMs(key: SpanKey): number {
  return SPANS.find((s) => s.key === key)?.ms ?? 21_600_000;
}

/* --------------------------------------------------------------------------
   Robot series colour.

   Colour is spent only when it disambiguates. One robot on screen needs no
   hue to tell it from another, so its trace takes the console's NOMINAL hue
   and nothing else does — which is §13 tell 28's rule applied to a plot
   rather than to a gauge: "non-breaching vitals share one neutral hue
   (`--ok`); the one that crosses its limit is the only thing that changes".
   A lane that breaches recolours in place, amber then red, and because the
   base is one flat blue that recolouring is the only hue change on the
   instrument — visible at a glance across nine lanes.

   (It was `--text-mid` for one round. A steel hairline is invisible at 1x on
   a 45px lane, and it also made "nothing is wrong" and "no data" the same
   ink, which is exactly what §3's grey rule forbids.)

   From two robots up each takes a slot on the --viz ramp — whose first slot
   IS `--ok`, so the subject's colour does not move when a comparand is added
   — and keeps it in every card, so a lane in the Battery panel and a lane in
   the CPU panel are the same robot without reading either label.
   -------------------------------------------------------------------------- */

const RAMP = [
  'var(--viz-1)', 'var(--viz-2)', 'var(--viz-3)', 'var(--viz-4)',
  'var(--viz-5)', 'var(--viz-6)', 'var(--viz-7)', 'var(--viz-8)',
];

export function seriesColour(index: number, total: number): string {
  if (total <= 1) return 'var(--ok)';
  return RAMP[index % RAMP.length]!;
}
